import React, { useState } from 'react';
import { Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useClaimGiftMutation, useGetUserGiftTransactionsQuery } from '../../../../store/api/giftApi';
import Loader from '../../../common/Loader';

const GiftsHistory = () => {
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [claimGiftCode, setClaimGiftCode] = useState('');
    const [claimGiftError, setClaimGiftError] = useState('');
    const [claimGift, { isLoading: isClaimingGift }] = useClaimGiftMutation();
    const selectedUser = useSelector(state => state.userManagement.selectedUser);
    const userData = useSelector(state => state.userManagement.userData) || {};
    const resolvedUserId = useSelector(state => state.userManagement.resolvedUserId);

    const selectedUserId =
        resolvedUserId ||
        userData?.user_id ||
        selectedUser?.user_id ||
        selectedUser?.id ||
        null;

    const pubId = localStorage.getItem('pub_id');

    const {
        data: giftTransactionsResponse,
        isLoading,
        isFetching,
        isError
    } = useGetUserGiftTransactionsQuery(
        {
            pubId,
            selectedUserId
        },
        {
            skip: !selectedUserId || !pubId
        }
    );

    const gifts = giftTransactionsResponse?.data || [];
    const loading = isLoading || isFetching;

    const getRowKey = (gift, index) => `${gift.giftcode || gift.plan_name || 'gift'}-${gift.purchase_date || index}-${index}`;

    const getStatusBadge = (status) => {
        const statusMap = {
            PENDING: 'warning',
            ACTIVE: 'success',
            EXPIRED: 'danger',
            REDEEMED: 'info'
        };
        return <Badge bg={statusMap[status] || 'secondary'}>{status}</Badge>;
    };

    const handleOpenClaimModal = () => {
        setClaimGiftError('');
        setShowClaimModal(true);
    };

    const handleCloseClaimModal = () => {
        setShowClaimModal(false);
        setClaimGiftCode('');
        setClaimGiftError('');
    };

    const handleClaimGift = async () => {
        const trimmedGiftCode = claimGiftCode.trim();
        setClaimGiftError('');

        if (!trimmedGiftCode) {
            setClaimGiftError('Please enter a gift code');
            return;
        }

        try {
            const response = await claimGift({
                selectedUserId,
                giftCode: trimmedGiftCode
            }).unwrap();

            if (response?.success) {
                toast.success(response?.message || 'Gift claimed successfully');
                handleCloseClaimModal();
                return;
            }

            setClaimGiftError(response?.message || 'Failed to claim gift');
        } catch (error) {
            const apiMessage = error?.data?.message || '';
            const isAlreadyRedeemed =
                error?.status === 400 &&
                typeof apiMessage === 'string' &&
                apiMessage.toLowerCase().includes('redeem');

            if (isAlreadyRedeemed) {
                setClaimGiftError(apiMessage || 'This gift code is already redeemed.');
                return;
            }

            toast.error(apiMessage || 'Failed to claim gift');
        }
    };

    if (loading) return <Loader />;

    if (!selectedUserId) return <div>Please select a user to view gift transactions.</div>;

    if (isError) return <div>Failed to load gift transactions.</div>;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="mb-0">Gift History</h3>
                <Button variant="primary" size="sm" onClick={handleOpenClaimModal}>Claim Gift</Button>
            </div>
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Gift Code</th>
                        <th>Purchase Date</th>
                        <th>Redeemed Date</th>
                        {/* <th>Period</th> */}
                        <th>Plan Name</th>
                        <th>Buyer</th>
                        <th>Recipient</th>
                        <th>Price</th>
                    </tr>
                </thead>
                <tbody>
                    {gifts.length === 0 && (
                        <tr>
                            <td colSpan={8} className="text-center">No gift transactions found</td>
                        </tr>
                    )}
                    {gifts.map((gift, index) => (
                        <tr key={getRowKey(gift, index)}>
                            <td>{getStatusBadge(gift.status || '-')}</td>
                            <td><code>{gift.giftcode || '-'}</code></td>
                            <td>{gift.purchase_date || '-'}</td>
                            <td>{gift.redeemed_date || '-'}</td>
                            {/* <td>{gift.period || '-'}</td> */}
                            <td>{gift.plan_name || '-'}</td>
                            <td>{gift.buyer_info || '-'}</td>
                            <td>{gift.recipient_info || '-'}</td>
                            <td>{gift.price || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal show={showClaimModal} onHide={handleCloseClaimModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Claim Gift</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-center">
                        <Form.Group className="w-100 mb-0">
                            <Form.Control
                                type="text"
                                placeholder="Enter gift code"
                                value={claimGiftCode}
                                onChange={event => {
                                    setClaimGiftCode(event.target.value);
                                    if (claimGiftError) setClaimGiftError('');
                                }}
                                className="text-center"
                            />
                            {claimGiftError && (
                                <Form.Text className="text-danger text-center d-block mt-2">
                                    {claimGiftError}
                                </Form.Text>
                            )}
                        </Form.Group>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleClaimGift} disabled={isClaimingGift}>
                        Claim
                    </Button>
                    <Button variant="secondary" onClick={handleCloseClaimModal} disabled={isClaimingGift}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default GiftsHistory;